import type { ReactNode } from 'react';
import { AlertTriangle, Circle } from 'lucide-react';
import { cn } from './utils';

/**
 * RUF-521 / Health Score Confidence — shared telemetry health-score call-out,
 * rendered on both the ops (dark theme-ops) and customer (light) asset-detail
 * pages under the FMV values.
 *
 * Confidence-aware treatment (approved design "3a"): **coverage is the single
 * source of truth** for how assertively the score is presented. An asset can
 * score 96 while only 5% of telemetry attributes report — the old ring/chips
 * overclaimed certainty and mislabeled *missing* data as *failing*. Now:
 *   - the ring encodes coverage, not score (solid arc length = coverage %);
 *   - the score number + "Good" word only earn green at high coverage;
 *   - a tier glyph (alert → hollow circle → none) sits under the number;
 *   - non-reporting subsystems (coverage 0) render as neutral dashed "no data"
 *     chips, never as failing red zeros.
 *
 * Tiers mirror the FMV confidence-dot convention and derive purely from
 * coverage: high > 80%, moderate 40–80%, low < 40%.
 *
 * Colors: tier accents use Tailwind red/amber (design #ef4444 / #f59e0b) which
 * read on both themes; the earned green is theme-aware (emerald on light,
 * design sage #8fbf9f on dark ops). Structural text uses semantic tokens
 * (foreground / muted-foreground / border) so it flips per theme.
 */

const COVERAGE_THRESHOLD = 0.5;

/** Kept for existing callers — coverage-gate at the shared 0.5 mark. */
export function hasSufficientScoreCoverage(coverage: number): boolean {
  return coverage >= COVERAGE_THRESHOLD;
}

type ScoreBand = 'good' | 'fair' | 'poor';

/** Score → band. Drives only the status *word* ("Good"/"Fair"/"Poor"); the
 * visual assertiveness (color, glyph, ring) is driven by coverage tier. */
export function scoreBandFor(score: number): ScoreBand {
  if (score >= 80) return 'good';
  if (score >= 50) return 'fair';
  return 'poor';
}

const BAND_LABEL: Record<ScoreBand, string> = {
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

type Tier = 'low' | 'moderate' | 'high';

/** Coverage (0–1) → tier. Single source of truth for the whole call-out. */
export function coverageTier(coverage: number): Tier {
  if (coverage > 0.8) return 'high';
  if (coverage >= 0.4) return 'moderate';
  return 'low';
}

/** Tailwind text-color class per tier — applied to the ring (via
 * stroke=currentColor), number, and status word. Green is theme-aware. */
export const TIER_ACCENT: Record<Tier, string> = {
  // SIL-338: red-500 measured ~3.76:1 on a white light-theme card, below
  // WCAG AA's 4.5:1 for normal text — red-600 clears ~4.8:1. Dark mode's
  // near-black card needs the opposite direction (red-600 alone would drop
  // to ~3.6:1 there), so it keeps a brighter red-400 (~6.3:1).
  low: 'text-red-600 dark:text-red-400',
  moderate: 'text-amber-500',
  high: 'text-emerald-600 dark:text-[#8fbf9f]',
};

export function formatScoreCoverageCaption(coverage: number): string {
  const pct = Math.round(coverage * 100);
  if (!hasSufficientScoreCoverage(coverage)) {
    return `Low confidence · ${pct}% coverage`;
  }
  return `${pct}% coverage`;
}

export function formatScoreCategoryLabel(category: string): string {
  return category.replace(/_/g, ' ');
}

function formatScoreDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const RING_SIZE = 96;
const RING_RADIUS = 40;
const RING_STROKE = 7;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS; // ≈ 251.33

/** Coverage ring — solid arc length encodes coverage % over a faint dashed
 * track, with the score number and tier glyph centered. */
export function CoverageRing({
  score,
  coverage,
  tier,
}: {
  score: number;
  coverage: number;
  tier: Tier;
}) {
  const clampedCov = Math.max(0, Math.min(1, coverage));
  const dash = clampedCov * RING_CIRCUMFERENCE;
  const numberColor = tier === 'high' ? TIER_ACCENT.high : 'text-muted-foreground';

  return (
    <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        className="-rotate-90"
      >
        {/* faint dashed coverage track */}
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth={RING_STROKE}
          strokeDasharray="2 6"
        />
        {/* solid coverage arc, tier-colored via currentColor */}
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={RING_STROKE}
          strokeDasharray={`${dash} ${RING_CIRCUMFERENCE - dash}`}
          strokeLinecap="round"
          className={TIER_ACCENT[tier]}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={cn('font-num font-semibold leading-none', numberColor)}
          style={{ fontSize: tier === 'high' ? 26 : 24 }}
        >
          {Math.round(Math.max(0, Math.min(100, score)))}
        </span>
        {tier === 'low' && (
          <AlertTriangle className="mt-0.5 size-3.5 text-red-400" strokeWidth={2.2} />
        )}
        {tier === 'moderate' && (
          <Circle className="mt-0.5 size-3 text-amber-400" strokeWidth={2.4} />
        )}
      </div>
    </div>
  );
}

/** Confidence chip next to the eyebrow. Low = filled red, moderate = amber
 * outline, high = no chip (a green "Good" word is shown instead). */
function ConfidenceChip({ tier }: { tier: Tier }) {
  if (tier === 'high') return null;
  const cls =
    tier === 'low'
      ? 'border-red-500/40 bg-red-500/10 text-red-400'
      : 'border-amber-500/45 text-amber-400';
  const label = tier === 'low' ? 'Low confidence' : 'Moderate confidence';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
        cls,
      )}
    >
      {tier === 'low' && <AlertTriangle className="size-[11px]" strokeWidth={2.2} />}
      {label}
    </span>
  );
}

/** Subsystem chip. Reporting = solid sage pill with its score; not reporting
 * (coverage 0) = neutral dashed "— no data" pill. Never a red zero. */
export function HealthScoreCategoryChip({
  category,
  score,
  coverage,
}: {
  category: string;
  score: number;
  coverage: number;
}) {
  const label = formatScoreCategoryLabel(category);
  if (coverage <= 0) {
    return (
      <span className="inline-flex items-center rounded-full border border-dashed border-muted-foreground/40 px-3 py-1 text-[13px] font-normal text-muted-foreground">
        {label} — no data
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#a9cdb9] px-3 py-1 text-[13px] font-medium text-[#14532d]">
      {label} <span className="font-num">{Math.round(score)}</span>
    </span>
  );
}

export interface TelemetryScoreCalloutProps {
  overall: number;
  coverage: number;
  scoredAt: string;
  categories: Array<{ category: string; score: number; coverage: number }>;
  /** Eyebrow label. Defaults to "Telemetry Health Score" (the FMV call-out).
   * The ops Health Score tab passes "Overall health score". */
  label?: string;
  /** Replaces the default "Scored {date} · ring shows telemetry coverage"
   * line — lets the ops panel show its model/source-day metadata instead. */
  metaLine?: ReactNode;
  className?: string;
}

export function TelemetryScoreCallout({
  overall,
  coverage,
  scoredAt,
  categories,
  label = 'Telemetry Health Score',
  metaLine,
  className,
}: TelemetryScoreCalloutProps) {
  const tier = coverageTier(coverage);
  const band = BAND_LABEL[scoreBandFor(overall)];
  const confidenceValue = Math.round(Math.max(0, Math.min(1, coverage)) * 100);
  const confidenceColor =
    tier === 'low' ? 'text-red-400' : tier === 'moderate' ? 'text-amber-400' : 'text-foreground';

  return (
    <div className={className}>
      <div className="flex items-center gap-5">
        <CoverageRing score={overall} coverage={coverage} tier={tier} />
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {label}
            </span>
            {tier === 'high' ? (
              <span className={cn('text-[13px] font-semibold', TIER_ACCENT.high)}>{band}</span>
            ) : (
              <ConfidenceChip tier={tier} />
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {tier !== 'high' && (
              <>
                Provisionally <span className="font-medium text-foreground">{band}</span> ·{' '}
              </>
            )}
            Confidence <span className={cn('font-num', confidenceColor)}>{confidenceValue} / 100</span>
          </div>
          <div className="text-[13px] text-muted-foreground">
            {metaLine ?? <>Scored {formatScoreDate(scoredAt)} · ring shows telemetry coverage</>}
          </div>
        </div>
      </div>
      {categories.length > 0 && (
        <div className="mt-[18px] flex flex-wrap gap-2">
          {categories.map((c) => (
            <HealthScoreCategoryChip
              key={c.category}
              category={c.category}
              score={c.score}
              coverage={c.coverage}
            />
          ))}
        </div>
      )}
    </div>
  );
}
